import { omit } from 'ramda'
import { useHistory, useLocation } from 'react-router-dom'

import { FilteringValue } from '../../../types/filtering'
import { SortingDirection } from '../../../types/sorting'
import { getQueryString, parseQueryString } from '../../../utils/queryString'
import { deserializeSorting, serializeSorting } from '../../../utils/sorting'

/** Enables filtering and sorting using URL as filters state. */
export const useQueryParamFiltering = <
  Keys extends string | number | symbol
>() => {
  const history = useHistory()
  const { search } = useLocation()

  const query = parseQueryString(search)
  const ordering = query.ordering || ''
  const sorting = deserializeSorting(ordering)
  const filtering = omit(['ordering'], query)

  // Sorting is single column: the clicked key replaces the whole `ordering`
  // instead of being appended to it. The server applies `ordering` left to
  // right, so a leftover key — `id` above all, being unique — would keep
  // winning over the column just clicked and the click would look inert.
  const handleSort = (key: Keys, direction: SortingDirection) => {
    history.push({
      pathname: history.location.pathname,
      search: getQueryString({
        ...query,
        ordering: serializeSorting({ [key]: direction }),
      }),
    })
  }

  const handleFilter = (key: Keys, value: FilteringValue) =>
    history.push({
      pathname: history.location.pathname,
      search: getQueryString({
        ...query,
        [key]: value,
      }),
    })

  return { filtering, sorting, handleSort, handleFilter }
}
